/**
 * V60.1: HrTransfersPage - 전배 관리 페이지
 *
 * HR이 전배 요청을 관리하는 페이지입니다.
 * approvalRequests(type='transfer') 단일 SSoT.
 *
 * **탭 구성:**
 * 1. HR승인대기: orgApproved 상태 (보내는 조직장 승인 완료, HR 승인 대기)
 * 2. 예약대기: approved_scheduled 상태 (HR 승인됨, 시행일 대기)
 * 3. 완료: applied 상태 (전배 완료)
 * 4. 반려: rejected 상태
 *
 * V64.11: Tailwind → inline styles 전환 (CSS 미적용 버그 수정)
 */

import React, { useState } from 'react';
import {
  ArrowRightLeft,
  AlertCircle,
  CheckCircle,
  XCircle,
  UserCheck,
  Calendar,
  ClipboardCheck,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  useTransferRequests,
  TRANSFER_STATUS_LABELS,
  TRANSFER_STATUS_COLORS,
} from '../../shared/hooks/useTransferRequests';
import { useTransferRequestMutations } from '../../shared/hooks/useTransferRequestMutations';
import { useEmployees } from '../../shared/hooks/useEmployees';
import { useOrgUnitsPublic } from '../../shared/hooks/useOrgUnitsPublic';
import { useAuthorization } from '../../shared/hooks/useAuthorization';
import { ApprovalRequest } from '../../shared/types/approvalRequest';
import { formatTransferEffectiveDate } from '../../shared/utils/dateUtils';  // V66: 전배일(실제 진행/예정일) 공용 포맷터

type TabType = 'hr_pending' | 'scheduled' | 'completed' | 'rejected';

const TABS: { key: TabType; label: string; icon: React.ReactNode }[] = [
  { key: 'hr_pending', label: '인사팀 승인대기', icon: <UserCheck size={15} /> },
  { key: 'scheduled', label: '예약대기', icon: <Calendar size={15} /> },
  { key: 'completed', label: '완료', icon: <CheckCircle size={15} /> },
  { key: 'rejected', label: '반려', icon: <XCircle size={15} /> },
];

export function HrTransfersPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuthorization();
  const isHR = hasPermission('hr.manage');

  const [activeTab, setActiveTab] = useState<TabType>('hr_pending');

  // V60.x: BE callable 기반 전배 요청 조회 (서버에서 회사 스코프 확정)
  const { transferRequests, isLoading, error, refetch } = useTransferRequests({});

  const { orgUnits } = useOrgUnitsPublic();
  const { hrConfirm, hrReject, isSubmitting } = useTransferRequestMutations();

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [formError, setFormError] = useState('');

  const getEmployeeName = (req: ApprovalRequest) => {
    const rd = req.requestData || {};
    if (rd.employeeName) return rd.employeeName;
    const targetUserId = req.transferTargetUserId || rd.targetUserId;
    return targetUserId || '-';
  };

  const getOrgUnitName = (orgUnitId: string, req: ApprovalRequest, isFrom: boolean) => {
    const rd = req.requestData || {};
    if (isFrom && rd.fromOrgUnitName) return rd.fromOrgUnitName;
    if (!isFrom && rd.toOrgUnitName) return rd.toOrgUnitName;
    const org = orgUnits.find(o => o.id === orgUnitId);
    return org?.name || '-';
  };

  // 탭별 필터링
  const getFilteredRequests = (): ApprovalRequest[] => {
    switch (activeTab) {
      case 'hr_pending':
        return transferRequests.filter(r => r.status === 'orgApproved');
      case 'scheduled':
        return transferRequests.filter(r => r.status === 'approved_scheduled');
      case 'completed':
        return transferRequests.filter(r => r.status === 'applied');
      case 'rejected':
        // V314: HR 단계 반려 건만 표시 (orgApprovedAt 존재 = 조직장 승인 후 인사팀까지 올라온 건)
        return transferRequests.filter(r => r.status === 'rejected' && r.orgApprovedAt);
      default:
        return [];
    }
  };

  // HR 승인 처리
  const handleHRConfirm = async (req: ApprovalRequest) => {
    const mode = req.transferMode || req.requestData?.transferMode;
    const effectiveDate = req.transferEffectiveDate || req.requestData?.effectiveDate;
    const modeLabel = mode === 'scheduled'
      ? `예약 전배 (시행일: ${effectiveDate})`
      : '즉시 전배';
    if (!window.confirm(`이 전배 요청을 승인하시겠습니까?\n\n모드: ${modeLabel}`)) return;

    try {
      setFormError('');
      await hrConfirm(req.id);
      const msg = mode === 'scheduled'
        ? `전배 예약 승인 완료 (시행일: ${effectiveDate})`
        : '전배 즉시 승인 완료. 조직 이동이 실행되었습니다.';
      setSuccessMessage(msg);
      refetch();
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err: any) {
      console.error('HR 승인 실패:', err);
      setFormError(err?.message || '인사팀 승인 처리에 실패했습니다.');
    }
  };

  // HR 반려 처리
  const handleHRReject = async (req: ApprovalRequest) => {
    const reason = prompt('반려 사유를 입력해주세요:');
    if (!reason) return;

    try {
      setFormError('');
      await hrReject(req.id, reason);
      setSuccessMessage('전배 요청을 반려했습니다.');
      refetch();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('HR 반려 실패:', err);
      setFormError(err?.message || '인사팀 반려 처리에 실패했습니다.');
    }
  };

  const filteredRequests = getFilteredRequests();

  // 상태별 뱃지 스타일
  const getStatusStyle = (status: string) => {
    const c = TRANSFER_STATUS_COLORS[status] || 'gray';
    const colorMap: Record<string, { color: string; bg: string }> = {
      blue: { color: '#2563eb', bg: '#eff6ff' },
      purple: { color: '#7c3aed', bg: '#f3e8ff' },
      green: { color: '#16a34a', bg: '#f0fdf4' },
      orange: { color: '#d97706', bg: '#fffbeb' },
      red: { color: '#dc2626', bg: '#fef2f2' },
      gray: { color: '#6b7280', bg: '#f9fafb' },
    };
    return colorMap[c] || colorMap.gray;
  };

  // V66 RCA: 로컬 formatDate 제거 (Timestamp 가 callable 응답에서 `{_seconds, _nanoseconds}` plain object 로
  // 와서 `new Date(obj)` 가 Invalid Date 를 반환 → "Invalid Date" 문자열이 화면에 노출됨).
  // 공용 `formatDateByCompanyCode` 사용 — Firestore Timestamp / `{_seconds,_nanoseconds}` / ISO 모두 안전 처리.

  // 탭별 건수
  const counts = {
    hr_pending: transferRequests.filter(r => r.status === 'orgApproved').length,
    scheduled: transferRequests.filter(r => r.status === 'approved_scheduled').length,
    completed: transferRequests.filter(r => r.status === 'applied').length,
    rejected: transferRequests.filter(r => r.status === 'rejected').length,
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
      {/* 헤더 */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>전배 관리</h2>
        <p style={{ margin: '6px 0 0', fontSize: 13, color: '#6b7280' }}>
          조직 간 직원 이동 요청을 관리합니다
        </p>
      </div>

      {/* 성공/에러 메시지 */}
      {successMessage && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0',
          borderRadius: 8, color: '#16a34a', fontSize: 13, marginBottom: 16,
        }}>
          <CheckCircle size={16} />
          {successMessage}
        </div>
      )}
      {formError && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca',
          borderRadius: 8, color: '#dc2626', fontSize: 13, marginBottom: 16,
        }}>
          <AlertCircle size={16} />
          {formError}
        </div>
      )}

      {/* 탭 네비게이션 */}
      <div style={{
        display: 'flex', gap: 0,
        borderBottom: '2px solid #e5e7eb', marginBottom: 24,
      }}>
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '10px 20px', fontSize: 14,
              fontWeight: activeTab === tab.key ? 600 : 400,
              color: activeTab === tab.key ? '#2563eb' : '#6b7280',
              background: 'transparent', border: 'none',
              borderBottom: activeTab === tab.key ? '2px solid #2563eb' : '2px solid transparent',
              marginBottom: -2, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            {tab.icon}
            {tab.label}
            {(counts as any)[tab.key] > 0 && (
              <span style={{
                background: tab.key === 'hr_pending' ? '#dc2626' : '#6b7280',
                color: 'white', borderRadius: 10,
                padding: '1px 7px', fontSize: 11, fontWeight: 600,
                minWidth: 18, textAlign: 'center',
              }}>
                {(counts as any)[tab.key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* 로딩 */}
      {isLoading && (
        <div style={{ padding: 48, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
          전배 요청 목록을 불러오는 중...
        </div>
      )}

      {/* 에러 */}
      {error && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca',
          borderRadius: 8, color: '#dc2626', fontSize: 13, marginBottom: 16,
        }}>
          <AlertCircle size={16} />
          데이터 조회 실패: {error.message}
        </div>
      )}

      {/* 전배 요청 목록 테이블 */}
      {!isLoading && !error && (
        <div style={{
          background: 'white', borderRadius: 12, border: '1px solid #e5e7eb',
          padding: 24, marginBottom: 24,
        }}>
          {filteredRequests.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
              해당하는 전배 요청이 없습니다.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>직원</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>조직 이동</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>모드</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>전배 사유</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>반려 사유</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>상태</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>전배일</th>
                    {activeTab === 'hr_pending' && (
                      <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>작업</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredRequests.map(req => {
                    const ss = getStatusStyle(req.status);
                    const fromOrgId = req.transferFromOrgUnitId || req.requestData?.fromOrgUnitId || '';
                    const toOrgId = req.transferToOrgUnitId || req.requestData?.toOrgUnitId || '';
                    const mode = req.transferMode || req.requestData?.transferMode;
                    const effDate = req.transferEffectiveDate || req.requestData?.effectiveDate;
                    return (
                      <tr key={req.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '10px 12px' }}>
                          <button
                            onClick={() => navigate(`/hr/employees/${req.transferTargetUserId || req.requestData?.targetUserId}`, { state: { from: '/hr/transfers' } })}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer',
                              color: '#111827', fontWeight: 500, fontSize: 14, padding: 0,
                            }}
                          >
                            {getEmployeeName(req)}
                          </button>
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <span style={{ color: '#6b7280' }}>{getOrgUnitName(fromOrgId, req, true)}</span>
                          <span style={{ margin: '0 6px', color: '#d1d5db' }}>&rarr;</span>
                          <span style={{ color: '#2563eb', fontWeight: 500 }}>{getOrgUnitName(toOrgId, req, false)}</span>
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                          {mode === 'scheduled' ? (
                            <span style={{ color: '#d97706', fontSize: 13 }}>예약 ({effDate})</span>
                          ) : (
                            <span style={{ color: '#6b7280', fontSize: 13 }}>즉시</span>
                          )}
                        </td>
                        <td style={{ padding: '10px 12px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {req.requestData?.reason || '-'}
                        </td>
                        <td style={{ padding: '10px 12px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: req.status === 'rejected' ? '#dc2626' : '#9ca3af' }}>
                          {req.status === 'rejected' ? (req.comment || '-') : '-'}
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                          <span style={{
                            display: 'inline-block', padding: '2px 10px', borderRadius: 12,
                            fontSize: 12, fontWeight: 500, color: ss.color, background: ss.bg,
                          }}>
                            {TRANSFER_STATUS_LABELS[req.status] || req.status}
                          </span>
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
                          {formatTransferEffectiveDate(req)}
                        </td>
                        {activeTab === 'hr_pending' && (
                          <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
                              <button
                                onClick={() => handleHRConfirm(req)}
                                disabled={isSubmitting}
                                title="인사팀 승인"
                                style={{
                                  padding: '6px 12px', background: '#2563eb', color: 'white',
                                  border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 500,
                                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                  display: 'flex', alignItems: 'center', gap: 4,
                                  opacity: isSubmitting ? 0.5 : 1,
                                }}
                              >
                                <UserCheck size={14} />
                                승인
                              </button>
                              <button
                                onClick={() => handleHRReject(req)}
                                disabled={isSubmitting}
                                title="인사팀 반려"
                                style={{
                                  padding: '6px 12px', background: 'white', color: '#dc2626',
                                  border: '1px solid #fca5a5', borderRadius: 6, fontSize: 13, fontWeight: 500,
                                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                  display: 'flex', alignItems: 'center', gap: 4,
                                  opacity: isSubmitting ? 0.5 : 1,
                                }}
                              >
                                <XCircle size={14} />
                                반려
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
